import { useCallback, useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import uuid from "uuid";

import type { APIError } from "simplydo/core/api";
import { SubscribedWebsocket } from "simplydo/core/api/websocket";
import { initUseQuery } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";

import api, { websocketApi } from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { useIdeaIdState } from "./useIdea";

const maxHistoryLength = 500;

type IdeaHistorySnapshot = {
  templated?: OpenAPI.Schemas["Idea"]["templated"];
  name?: OpenAPI.Schemas["Idea"]["name"];
  coverImage?: OpenAPI.Schemas["Idea"]["coverImage"];
};

export const reducePath = <T,>(
  obj: T,
  changes: Partial<T>,
  path: string[],
): Partial<T> => {
  if (path.length === 0) {
    return changes;
  }
  const defaultObj = obj ?? ({} as T);
  const item = path[0];
  const nextObj = defaultObj[item as keyof T] ?? ({} as T);

  return {
    ...defaultObj,
    [item as keyof T]: reducePath(nextObj, changes, path.slice(1)),
  };
};

export const useIdeaEditor = () => {
  const [ideaId] = useIdeaIdState();

  const [historyState, setHistoryState] = useState<{
    undo: IdeaHistorySnapshot[];
    redo: IdeaHistorySnapshot[];
  }>({ undo: [], redo: [] });
  const [websocketState, setWebsocketState] = useState<{
    otherUsers: Record<string, any>;
    websocketConnected: boolean;
    externalChanges?: Record<string, any>;
  }>({ otherUsers: {}, websocketConnected: true });
  const [unsaved, setUnsaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | "">("");
  const editTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ideaAtStartOfUpdate = useRef<OpenAPI.Schemas["Idea"] | null>(null);
  const websocketSubscription = useRef<SubscribedWebsocket | null>(null);
  const currentSave = useRef("");
  const updatedAt = useRef(new Date());
  const user = useAppSelector((state) => state.user);
  const userId = user?._id;
  const { t } = useTranslation();

  const ideaQuery = useQuery(
    initUseQuery(api.ideas.get, [ideaId!], {
      isEnabled: !!ideaId,
      placeholderData: keepPreviousData,
    }),
  );
  const idea = ideaQuery.data;

  const ideaState = {
    unsaved,
    isSaving,
    savedAt,
    notFound: ideaQuery.isError,
    errorMessage: (ideaQuery.error as APIError | null)?.message ?? "",
  };

  const isCollaborator = idea?.authors?.find((author) => author._id === userId);

  const reloadIdea = useCallback(() => {
    api.invalidateQueries(api.ideas.get, [ideaId]);
  }, [ideaId]);

  useEffect(() => {
    setHistoryState({ undo: [], redo: [] });
    setUnsaved(false);
    setSavedAt("");
    setWebsocketState({ otherUsers: {}, websocketConnected: true });
    ideaAtStartOfUpdate.current = null;
    if (editTimeout.current !== null) {
      clearTimeout(editTimeout.current);
      editTimeout.current = null;
    }
  }, [ideaId]);

  const saveIdea = useCallback(
    (changes = {}) => {
      const fetchId = uuid.v4();
      currentSave.current = fetchId;
      const savedAtTime = new Date();

      setIsSaving(true);
      return api.ideas.update(
        ideaId,
        changes,
        ({
          templated,
          ownerIdeaTemplate: _remove,
          name: _remove2,
          ...serverChanges
        }) => {
          if (currentSave.current !== fetchId) {
            return;
          }

          api.setQueryData(api.ideas.get, [ideaId], (prev) => {
            const previous = prev ?? {};
            return {
              ...previous,
              ...serverChanges,
              templated:
                updatedAt.current > savedAtTime ?
                  previous.templated
                : templated,
            };
          });
          setIsSaving(false);
          setUnsaved(false);
          setSavedAt(savedAtTime);
        },
        (err) => {
          if (err.errorCode === "ideaIsSubmitted") {
            toast.error(
              `This ${t("generic.idea")} cannot be edited because it has been submitted.`,
            );

            setIsSaving(false);
            reloadIdea();
          } else {
            toast.error(err.message);
            setIsSaving(false);
          }
        },
      );
    },
    [ideaId, t, reloadIdea],
  );

  const asHistoryState = useCallback(
    (
      historyIdea: Pick<
        OpenAPI.Schemas["Idea"],
        "templated" | "name" | "coverImage"
      >,
    ) => ({
      templated: historyIdea.templated,
      name: historyIdea.name,
      coverImage: historyIdea.coverImage,
    }),
    [],
  );

  const addHistoryEntry = useCallback(
    (updatedIdea: Partial<OpenAPI.Schemas["Idea"]>) => {
      setHistoryState((prevHistory) => {
        const slicedUndoHistory = prevHistory.undo.slice(
          Math.max(prevHistory.undo.length - maxHistoryLength, 0),
        );
        return {
          ...prevHistory,
          undo: [...slicedUndoHistory, asHistoryState(updatedIdea)],
        };
      });
    },
    [asHistoryState],
  );

  const undoChanges = useCallback(() => {
    if (historyState.undo.length === 0 || !idea) {
      return;
    }
    const undoItem = historyState.undo[historyState.undo.length - 1];
    api.setQueryData(api.ideas.get, [ideaId], (prev) => ({
      ...prev,
      ...undoItem,
    }));
    saveIdea(undoItem);
    setHistoryState((prevHistory) => ({
      redo: [...prevHistory.redo, asHistoryState(idea)],
      undo: prevHistory.undo.slice(0, -1),
    }));
  }, [historyState.undo, idea, ideaId, saveIdea, asHistoryState]);

  const redoChanges = useCallback(() => {
    if (historyState.redo.length === 0 || !idea) {
      return;
    }
    const redoItem = historyState.redo[historyState.redo.length - 1];
    api.setQueryData(api.ideas.get, [ideaId], (prev) =>
      prev ? { ...prev, ...redoItem } : prev,
    );
    saveIdea(redoItem);
    setHistoryState((prevHistory) => ({
      redo: prevHistory.redo.slice(0, -1),
      undo: [...prevHistory.undo, asHistoryState(idea)],
    }));
  }, [historyState.redo, idea, ideaId, saveIdea, asHistoryState]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.keyCode === 90 && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) {
          redoChanges();
        } else {
          undoChanges();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [redoChanges, undoChanges]);

  /**
   * `update` can either be:
   *   - data which will be inserted into the idea at the path
   *   - a function which will be called with the current idea and
   *     should only return the data to be inserted at the path
   */
  const updateIdea = useCallback(
    (update: any, path: string[], suppressSave = false) => {
      if (update === true && path.includes("isSubmitted")) {
        websocketSubscription.current?.publish("ideaSubmitted");
      }

      let prevIdea = null;
      let updatedIdea = null;
      api.setQueryData(api.ideas.get, [ideaId], (prev) => {
        prevIdea = prev;

        if (!ideaAtStartOfUpdate.current) {
          ideaAtStartOfUpdate.current = prevIdea;
        }
        updatedIdea = reducePath(
          prevIdea,
          update,
          path,
        ) as OpenAPI.Schemas["Idea"];

        return updatedIdea;
      });

      if (suppressSave) {
        util.onUpdateIdea(updatedIdea, update, path);
        return;
      }

      updatedAt.current = new Date();
      setUnsaved(true);

      if (editTimeout.current !== null) {
        clearTimeout(editTimeout.current);
        editTimeout.current = null;
      }

      editTimeout.current = setTimeout(() => {
        const ideaToSave = updatedIdea;
        if (!ideaToSave) {
          return;
        }
        saveIdea(ideaToSave);
        util.onUpdateIdea(ideaToSave, update, path);
        websocketSubscription.current?.publish("fieldEdited", {
          fieldId: path,
          fieldData: update,
        });
        /**
         * If current history index is < 0, and we're making an edit
         * Then the user is continuing from an old edit (i.e. they undid a few times, then started editing again)
         * Therefore the history state should be reset, with the new state
         * The history index should also be reset to 0
         * Else we just add to the history
         */
        setHistoryState((prevHistory) => ({ ...prevHistory, redo: [] }));
        if (ideaAtStartOfUpdate.current) {
          addHistoryEntry(asHistoryState(ideaAtStartOfUpdate.current));
        }

        ideaAtStartOfUpdate.current = null;
        editTimeout.current = null;
      }, 400);
    },
    [ideaId, asHistoryState, addHistoryEntry, saveIdea],
  );

  const handleWebsocket = useCallback(
    (data: {
      channel: string;
      fieldId: string[];
      fieldData: any;
      toUser: string;
      user: { _id: string; name: string };
    }) => {
      const { channel, fieldId, fieldData, toUser } = data;
      // Discard any messages that were sent by the current user
      if (
        (toUser && toUser !== userId) ||
        (data.user && data.user._id === userId)
      ) {
        return;
      }

      switch (channel) {
        // Make sure that if a user opens an idea they get the proper response back if someone else was already in it to begin with
        case "userSubscribed":
          websocketSubscription.current?.publish("userWasSubscribed", {
            toUser: data.user._id,
          });
          setWebsocketState((prevState) => ({
            ...prevState,
            otherUsers: { ...prevState.otherUsers, [data.user._id]: data.user },
          }));
          break;
        case "userWasSubscribed":
          setWebsocketState((prevState) => ({
            ...prevState,
            otherUsers: { ...prevState.otherUsers, [data.user._id]: data.user },
          }));
          break;
        case "userUnsubscribed":
          setWebsocketState((prevState) => {
            const externalChanges = { ...(prevState.externalChanges || {}) };

            const updateDeep = (changes: Record<string, any>) => {
              Object.keys(changes).forEach((field) => {
                if (Array.isArray(changes[field])) {
                  changes[field] = [
                    ...changes[field].filter((u) => u._id !== data.user._id),
                  ];
                } else {
                  changes[field] = { ...updateDeep(changes[field]) };
                }
              });
              return changes;
            };
            updateDeep(externalChanges);

            const { [data.user._id]: _delUser, ...keepUsers } =
              prevState.otherUsers;
            return { ...prevState, otherUsers: keepUsers, externalChanges };
          });
          break;
        case "ideaSubmitted":
          reloadIdea();
          toast(`${data.user.name} submitted the ${t("generic.idea")}.`);
          break;
        case "fieldEdited":
          setWebsocketState((prevState) => {
            const externalChanges = { ...(prevState.externalChanges || {}) };
            let change = externalChanges;
            fieldId.forEach((field, i) => {
              if (i < fieldId.length - 1) {
                change[field] = { ...(change[field] || {}) };
                change = change[field];
                return;
              }

              change[field] = [...(change[field] || [])];
              const index = change[field].findIndex(
                (u) => u._id === data.user._id,
              );
              if (index === -1) {
                change[field].push({
                  ...data.user,
                  isEditing: { fieldId, fieldData },
                });
              } else {
                change[field][index] = {
                  ...data.user,
                  isEditing: { fieldId, fieldData },
                };
              }
            });

            return {
              ...prevState,
              otherUsers: {
                ...prevState.otherUsers,
                [data.user._id]: data.user,
              },
              externalChanges,
            };
          });
          updateIdea(fieldData, fieldId, true);
          break;
        case "__websocketError":
          setWebsocketState((prevState) => ({
            ...prevState,
            websocketConnected: false,
          }));
          break;
        default:
          break;
      }
    },
    [userId, updateIdea, reloadIdea, t],
  );

  // set up websocket connection for idea collaboration
  useEffect(() => {
    if (!ideaId) return;

    websocketSubscription.current = websocketApi.subscribe(
      `idea-${ideaId}`,
      (data) => handleWebsocket(data),
      {
        receiveUserUpdates: true,
        receiveErrorUpdates: true,
        quiet: !isCollaborator,
      },
    );

    return () => {
      if (websocketSubscription.current) {
        websocketSubscription.current.unsubscribe();
      }
      websocketSubscription.current = null;
    };
  }, [ideaId, isCollaborator, handleWebsocket]);

  return {
    ideaState,
    updateIdea,
    reloadIdea,
    undoChanges,
    redoChanges,
    canUndo: historyState.undo.length > 0,
    canRedo: historyState.redo.length > 0,
    otherUsers: websocketState.otherUsers,
    websocketConnected: websocketState.websocketConnected,
    externalChanges: websocketState.externalChanges,
  };
};
